import { Format } from './format';
import type { SharePart } from './share-part';

/** A divisão de um total em fatias nomeadas. O cartão lidera com a maior delas:
 *  fixar uma fatia no destaque mostra "39% entrada fria" num período em que a
 *  saída leva 49%, e quem lê conclui o contrário do que o número diz. */
export class CostShare {
  private constructor(
    private readonly ordered: readonly SharePart[],
    private readonly total: number,
  ) {}

  static of(parts: readonly SharePart[]): CostShare {
    const positive = parts.filter((part) => part.value > 0);
    const total = positive.reduce((sum, part) => sum + part.value, 0);
    return new CostShare([...positive].sort((a, b) => b.value - a.value), total);
  }

  get isEmpty(): boolean {
    return this.total <= 0;
  }

  /** "49% saída" — a fatia que manda no período. */
  get leader(): string {
    if (this.isEmpty) return '—';
    return `${this.shareOf(this.ordered[0])} ${this.ordered[0].label}`;
  }

  /** "entrada fria 39% · hit 12%" — as outras, da maior para a menor. */
  get rest(): string {
    return this.ordered
      .slice(1)
      .map((part) => `${part.label} ${this.shareOf(part)}`)
      .join(' · ');
  }

  /** Uma casa decimal quando a líder empata com a seguinte no número redondo:
   *  "46% saída" acima de "entrada fria 46%" faz o leitor duvidar da ordem. */
  private get casas(): number {
    const [primeira, segunda] = this.ordered;
    if (!segunda) return 0;
    return Format.percent(primeira.value, this.total) === Format.percent(segunda.value, this.total)
      ? 1
      : 0;
  }

  private shareOf(part: SharePart): string {
    return Format.percent(part.value, this.total, this.casas);
  }
}
